import { SxProps, Theme } from '@mui/material';
import {
	Colors,
	inputBackgroundColor,
	inputBorderColor,
	inputDisabledBackgroundColor,
	inputDisabledBorderColor,
	inputFocusBorderColor,
	inputForegroundColor,
	inputPlaceholderColor,
} from '../../constants';
import { clsx } from '../../utils/clsx';

export const inputFontSize = '14px';
export const inputBorderRadius = '8px';
export const inputWrapperMinHeight = '38px';
export const inputMinHeight = '20px';
export const inputPaddings = '10px 16px';

export const defaultTextInputStyles: SxProps<Theme> = {
	borderRadius: inputBorderRadius,
	color: inputForegroundColor,
	minHeight: inputWrapperMinHeight,
	maxWidth: 'none',
	fontSize: inputFontSize,

	'& fieldset': {
		borderColor: inputBorderColor,
		borderRadius: inputBorderRadius,
	},

	'&.MuiInputBase-root, & .MuiInputBase-root, &.MuiInputBase-multiline': {
		backgroundColor: inputBackgroundColor,
		borderRadius: inputBorderRadius,

		'& fieldset': {
			borderColor: inputBorderColor,
			borderRadius: inputBorderRadius,
		},

		'&.Mui-focused, &:hover, &:focus': {
			'& fieldset': {
				borderColor: inputFocusBorderColor,
			},
		},

		'&.Mui-disabled': {
			backgroundColor: inputDisabledBackgroundColor,

			'&.Mui-focused, &:hover, &:focus': {
				'& fieldset': {
					borderColor: `${inputDisabledBorderColor} !important`,
				},
			},
		},

		'&.MuiInputBase-adornedStart': {
			'&.MuiInputBase-multiline': {
				'& > svg': {
					alignSelf: 'flex-start',
					mt: '2px',
					flexShrink: 0,
				},
			},

			'& > svg': {
				flexShrink: 0,
			},
		},
	},

	'& .MuiOutlinedInput-input': {
		padding: inputPaddings,
		height: inputMinHeight,

		py: '10px',
		fontFamily: 'Montserrat, sans-serif',
		fontWeight: '500',
		fontSize: inputFontSize,
		lineHeight: '19.5px',

		'&::placeholder': {
			fontSize: inputFontSize,
			color: inputPlaceholderColor,
		},

		'&::-webkit-scrollbar': {
			width: '4px',
		},

		'&::-webkit-scrollbar-thumb': {
			backgroundColor: Colors.white_a20,
			borderRadius: '2px',
		},
	},

	'& .MuiInputBase-inputMultiline': {
		p: '0',
	},
};

export const defaultSelectInputStyles = (): SxProps<Theme> =>
	clsx(defaultTextInputStyles, {
		'& .MuiSelect-select': {
			flex: 1,
		},
	});

export const defaultSelectMenuStyles = (): SxProps<Theme> => ({
	background: Colors['1F1F1F'],
});

export const defaultSelectItemStyles = (): SxProps<Theme> => ({
	background: Colors.transparent,
	color: Colors.white,
	padding: '12px 16px',

	'& .MuiMenuItem-selected': {
		background: `linear-gradient(0deg, ${Colors.black_a40}, ${Colors.black_a40}, linear-gradient(0deg, ${Colors.white_a10}, ${Colors.white_a10})`,
	},
});

export const defaultAutoCompleteStyles = (hasValues = false): SxProps<Theme> =>
	clsx(defaultSelectInputStyles(), {
		'& .MuiAutocomplete-inputRoot, &.MuiAutocomplete-inputRoot': {
			minHeight: '40px',
			py: '10px !important',
			pl: '16px !important',
			rowGap: '8px',

			'& .MuiAutocomplete-input': {
				py: '0 !important',
				paddingRight: '0 !important',
				paddingLeft: '8px !important',
				height: '20px !important',
			},

			'& .MuiAutocomplete-tag': {
				boxSizing: 'border-box',
				minHeight: '31px',
				my: '0px',
			},

			'& fieldset': {
				border: '1px solid transparent',
			},
		},
	});

export const darkThemeVars = {
	'--baseBg': Colors.white_a10,
	'--basePageBg': Colors.black,
	'--baseTextContrast': Colors.white,
	'--baseBgActive': Colors.white_a5,
	'--baseBorderHover': Colors.white_a20,
	'--accentSolidHover': Colors.white_a80,
	'--radius-medium': '8px',
};

export const editorContainerStyles = (theme: Theme) => ({
	...darkThemeVars,
	'--baseBg': Colors.black,
	'--baseBorderHover': Colors.white,
	'--accentSolidHover': Colors.white,
	'--basePageBg': Colors['121212'],
	'--baseText': Colors.black,
	'--baseBase': inputBackgroundColor,
	'--baseBorder': inputBorderColor,
	'--baseBgActive': Colors.black_a20,
	'--radius-base': '8px',

	'& form > div > label[for=link-url] + div': {
		'& > div > input': {
			flexGrow: 1,
		},

		'& > div > ul': {
			backgroundColor: Colors['1A1A1A'],

			'& li': {
				color: Colors.white,
				fontFamily: 'Montserrat, sans-serif',
				fontWeight: '500',
				fontSize: '14px',

				'&[data-highlighted=true]': {
					backgroundColor: Colors.white_a10,
				},

				'&[data-selected=true]': {
					backgroundColor: Colors.white_a20,
				},
			},
		},
	},

	'& label': {
		color: Colors.white,
		fontFamily: 'Montserrat, sans-serif',
		fontWeight: '600',
	},

	'& input': {
		color: inputForegroundColor,
		fontFamily: 'Montserrat, sans-serif',
		fontWeight: '500',
		fontSize: '14px',

		'&::placeholder': {
			color: inputPlaceholderColor,
		},
	},

	'& button': {
		cursor: 'pointer',
	},

	'& button[type=submit]': {
		background: Colors.white,
		color: Colors.black,
		border: 'none',
		fontFamily: 'Montserrat, sans-serif',
		fontWeight: 'bold',
		padding: '5px 15px',
	},

	'& button[type=reset]': {
		background: 'transparent',
		color: Colors.white,
		fontFamily: 'Montserrat, sans-serif',
		fontWeight: 'bold',
		padding: '5px 15px',
	},
});

export const mdxEditorTheme = (
	theme: Theme,
	startAdornment: React.ReactNode | undefined,
	maxRows: number | undefined,
	minRows: number | undefined
): any => ({
	'& .mdxeditor': {
		...darkThemeVars,
		borderRadius: 'var(--radius-medium)',

		'& > .mdxeditor-toolbar': {
			backgroundColor: Colors['1A1A1A'],
			borderRadius: 'var(--radius-medium) var(--radius-medium) 0 0',
			borderBottom: `1px solid ${Colors.white_a10}`,
			top: '-10px',
			gap: '0px',

			'& > div': {
				marginRight: '0px',
				marginLeft: '0px',
			},

			button: {
				cursor: 'pointer',
				width: '28px',
				height: '28px',
				borderRadius: '2px !important',

				'&:hover': {
					backgroundColor: Colors.white_a10,
				},

				'&:active': {
					backgroundColor: Colors.white_a20,
				},

				'& span[data-state=delayed-open]': {
					color: Colors.black,
				},
			},
		},

		'& >.mdxeditor-root-contenteditable': {
			backgroundColor: 'var(--baseBg)',
			borderBottomLeftRadius: 'var(--radius-medium)',
			borderBottomRightRadius: 'var(--radius-medium)',
			fontFamily: 'Montserrat, sans-serif',
			fontSize: '14px',
			fontWeight: 500,
			lineHeight: 1.5,

			...(startAdornment
				? {
						borderRadius: 'var(--radius-medium)',
						paddingLeft: '30px',
				  }
				: {}),

			...(maxRows
				? {
						maxHeight: `${maxRows * 17 + 24}px`,
						overflow: 'hidden',
				  }
				: {}),

			...(minRows
				? {
						minHeight: `${minRows * 17 + 24}px`,
				  }
				: {}),

			'& a': {
				color: `${Colors.link} !important`,
			},

			'& p': {
				marginTop: 0,

				'&:last-child': {
					marginBottom: '0px !important',
				},
			},
		},
	},
});
